"use client";

import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Download, Loader2, Save } from "lucide-react";
import { useAdminSession } from "@/hooks/use-admin-session";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import {
  getTtpaPageForStaff,
  listEasyTtpaInterest,
  saveTtpaInfoPage,
} from "@/lib/module-ttpa.functions";
import { TTPA_LEGAL_NOTICE, interestListToCsv } from "@/lib/module-ttpa";
import { setModuleInterestHandled } from "@/lib/admin-dashboard.functions";

export const Route = createFileRoute("/_admin/admin/module-ttpa")({
  // Ticket AD4a: Sprungziel aus dem Arbeitsvorrat (?interessenten=offen).
  validateSearch: (search: Record<string, unknown>): { interessenten?: "offen" } => ({
    interessenten: search.interessenten === "offen" ? ("offen" as const) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "TTPA-Hinweisseite – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ModuleTtpaAdminPage,
});

const inputClass =
  "mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const labelClass = "block text-sm font-semibold text-foreground";

/** Ticket Z13: Pflege des Seitentexts und Auswertung der Interessentenliste. */
function ModuleTtpaAdminPage() {
  const session = useAdminSession();
  const isAdmin = session.isAdmin;
  const qc = useQueryClient();

  const pageFn = useServerFn(getTtpaPageForStaff);
  const saveFn = useServerFn(saveTtpaInfoPage);
  const listFn = useServerFn(listEasyTtpaInterest);
  const handledFn = useServerFn(setModuleInterestHandled);

  const { data: page, isLoading } = useQuery({
    queryKey: ["admin", "module-ttpa", "page"],
    queryFn: () => pageFn(),
  });
  const { data: interestAll } = useQuery({
    queryKey: ["admin", "module-ttpa", "interest"],
    queryFn: () => listFn(),
  });
  // Sprungziel aus dem Arbeitsvorrat: nur unbeantwortete Vormerkungen.
  const onlyOpenInterest = Route.useSearch().interessenten === "offen";
  const interest = onlyOpenInterest
    ? (interestAll ?? []).filter((row) => !row.handledAt)
    : interestAll;

  const handledMut = useMutation({
    mutationFn: (vars: { id: string; handled: boolean }) => handledFn({ data: vars }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "module-ttpa", "interest"] });
      void qc.invalidateQueries({ queryKey: ["admin", "dashboard-stats"] });
      toast.success("Status gespeichert.");
    },
    onError: (e: Error) => toast.error(e.message || "Speichern fehlgeschlagen."),
  });

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [isDraft, setIsDraft] = useState(true);

  useEffect(() => {
    if (!page) return;
    setTitle(page.title);
    setBody(page.body);
    setInternalNote(page.internalNote ?? "");
    setIsDraft(page.isDraft);
  }, [page]);

  const save = useMutation({
    mutationFn: () =>
      saveFn({
        data: {
          title: title.trim(),
          body,
          internalNote: internalNote.trim() ? internalNote.trim() : null,
          isDraft,
        },
      }),
    onSuccess: () => {
      toast.success("Inhalt gespeichert.");
      qc.invalidateQueries({ queryKey: ["admin", "module-ttpa", "page"] });
    },
    onError: () => toast.error("Speichern nicht möglich."),
  });

  const downloadCsv = () => {
    const csv = interestListToCsv(interest ?? []);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "easyttpa-interessenten.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold text-foreground">TTPA-Hinweisseite</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Inhalt der Modulseite „TTPA-Verordnung". Die Seite ist im Cockpit nur
          sichtbar, wenn das Feature-Flag <code>module_ttpa_info_visible</code> unter
          „Feature-Flags" eingeschaltet ist.
        </p>
      </header>

      {!isAdmin && (
        <p className="mb-4 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          Als Editorin oder Editor können Sie die Hinweisseite nur ansehen. Änderungen sind
          Administratoren vorbehalten.
        </p>
      )}


      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Wird geladen …
        </div>
      ) : (
        <section className="space-y-4 rounded-lg border border-border bg-background p-5">
          {isDraft && (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-foreground">
              Dieser Text ist als Entwurf gekennzeichnet und vor dem Einschalten des
              Flags anwaltlich zu prüfen.
            </p>
          )}

          <div>
            <label className={labelClass} htmlFor="ttpa-title">
              Überschrift
            </label>
            <input
              id="ttpa-title"
              className={inputClass}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!isAdmin}
            />
          </div>

          <div>
            <span className={labelClass}>Text</span>
            <div className="mt-1">
              <MarkdownEditor value={body} onChange={setBody} />
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="ttpa-note">
              Interne Notiz (nicht im Cockpit sichtbar)
            </label>
            <input
              id="ttpa-note"
              className={inputClass}
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              disabled={!isAdmin}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={isDraft}
              onChange={(e) => setIsDraft(e.target.checked)}
              disabled={!isAdmin}
            />
            Als Entwurf gekennzeichnet
          </label>

          <p className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
            Fester Seitenabschluss: „{TTPA_LEGAL_NOTICE}"
          </p>

          {isAdmin ? (
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending || !title.trim()}
              className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Save className="h-4 w-4" aria-hidden />
              )}
              Speichern
            </button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nur Administratoren können den Text ändern.
            </p>
          )}
        </section>
      )}

      <section className="space-y-4 rounded-lg border border-border bg-background p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Interessenten easyTTPA
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {interest?.length ?? 0} Vormerkung(en). Je Kunde entsteht genau ein
              Eintrag.
            </p>
          </div>
          <button
            type="button"
            onClick={downloadCsv}
            disabled={!interest || interest.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary disabled:opacity-50"
          >
            <Download className="h-4 w-4" aria-hidden />
            Als CSV ausgeben
          </button>
        </div>

        {interest && interest.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-semibold">Kunde</th>
                  <th className="py-2 pr-4 font-semibold">Kundennummer</th>
                  <th className="py-2 pr-4 font-semibold">Zeitpunkt</th>
                  <th className="py-2 font-semibold">Bearbeitung</th>
                </tr>
              </thead>
              <tbody>
                {interest.map((row) => (
                  <tr key={row.id} className="border-b border-border/60">
                    <td className="py-2 pr-4 text-foreground">{row.clientName}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {row.clientNumber ?? "–"}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {new Date(row.createdAt).toLocaleString("de-DE")}
                    </td>
                    <td className="py-2">
                      {isAdmin ? (
                        <button
                          type="button"
                          onClick={() =>
                            handledMut.mutate({ id: row.id, handled: !row.handledAt })
                          }
                          disabled={handledMut.isPending}
                          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-primary disabled:opacity-50"
                        >
                          {row.handledAt ? "Wieder öffnen" : "Als beantwortet markieren"}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {row.handledAt ? "Beantwortet" : "Offen"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Noch keine Vormerkungen.</p>
        )}
      </section>
    </div>
  );
}
